import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";

/**
 * Public marketing header — sticky, glass, large logo + premium tagline.
 * Nav targets are all EXISTING routes; the Features link scrolls to the
 * in-page #features section when already on the landing page.
 */
const NAV_LINKS = [];

const SaaSHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, path) => {
    if (!path.includes("#")) return;
    const id = path.split("#")[1];
    if (location.pathname === "/") {
      e.preventDefault();
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      setMobileMenuOpen(false);
    }
  };

  const renderLink = (link, mobile = false) => (
    <Link
      key={link.label}
      to={link.isAnchor ? "/" : link.path}
      onClick={(e) => {
        if (link.isAnchor) handleAnchor(e, link.path);
        if (mobile) setMobileMenuOpen(false);
      }}
      className={
        mobile
          ? "text-base font-semibold text-gray-700 hover:text-purple-600 py-1.5 transition-colors"
          : "text-[13.5px] font-semibold text-gray-600 hover:text-purple-700 transition-colors whitespace-nowrap"
      }
    >
      {link.label}
    </Link>
  );

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-gray-200/70 shadow-[0_1px_20px_rgba(109,74,255,0.06)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
      role="banner"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 h-[88px] flex items-center justify-between gap-6">

        {/* LOGO — primary branding element (lockup already carries the name + tagline) */}
        <Link to="/" className="flex items-center group flex-shrink-0" aria-label="The Skill Genie — Empowering Future Builders. Engineering Impact. — Home">
          <img
            src="https://skillgenie-college-uploads.s3.ap-south-1.amazonaws.com/TSG_Logo.png"
            alt="TSG — The Skill Genie. Empowering Future Builders. Engineering Impact."
            height="64"
            decoding="async"
            fetchpriority="high"
            className="h-12 sm:h-14 md:h-16 w-auto object-contain group-hover:scale-[1.03] transition-transform"
          />
        </Link>

        {/* DESKTOP NAV */}
        <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
          {NAV_LINKS.map((l) => renderLink(l))}
        </nav>

        {/* AUTH CTAs */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <Link
            to="/contact"
            className="hidden xl:block text-sm font-bold text-gray-700 hover:text-purple-700 transition-colors"
          >
            Contact Us
          </Link>
          <a
            href="https://www.theskillgenie.com/nextgen-builder/register"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden xl:inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-bold rounded-full bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:shadow-lg hover:shadow-purple-300/50 hover:-translate-y-0.5 transition-all"
          >
            Get Started
            <ArrowRight size={15} aria-hidden="true" />
          </a>

          {/* MOBILE TOGGLE */}
          <button
            className="xl:hidden p-2 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {mobileMenuOpen && (
        <div className="xl:hidden absolute top-[88px] left-0 w-full bg-white/95 backdrop-blur-xl border-b border-gray-100 shadow-2xl px-6 py-6">
          <nav className="flex flex-col gap-2" aria-label="Mobile">
            {NAV_LINKS.map((l) => renderLink(l, true))}
            <hr className="my-3 border-gray-100" />
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="text-base font-bold text-gray-800 py-1.5"
            >
              Contact Us
            </Link>
            <a
              href="https://www.theskillgenie.com/nextgen-builder/register"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-2 w-full py-3.5 text-white text-base font-bold rounded-xl bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 shadow-lg shadow-purple-200 text-center block"
            >
              Get Started
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default SaaSHeader;
